import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { BankingOnboardingFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AccountsPageCard } from 'modules/banking/components/AccountsPageCard'
import { useBankingAuthorization } from 'modules/banking/hooks/useBankingAuthorization'
import { bankLogo, getContinueOnboardingUrl, getDefaultAlias } from 'modules/banking/utils'
import { mapStatusToClass, mapStatusToText, messageFromStatus } from 'modules/banking/utils'

interface AccountOnboardingCardProps {
  onboarding: BankingOnboardingFragment
}

export const AccountOnboardingCard = ({ onboarding }: AccountOnboardingCardProps) => {
  const { employee } = useCurrentSession()
  const { getAuthorizationUrl } = useBankingAuthorization()
  const [showEmailModal, setShowEmailModal] = useState(false)

  const url = getContinueOnboardingUrl(onboarding, [])
  const status = onboarding.status
  const legalEntityName = onboarding.legalEntity.legalName || ''
  const linkText = messageFromStatus(onboarding.status)

  // Check if user needs to check their email instead of redirecting
  const needsEmailCheck = onboarding.requiredActions?.includes('check_email')

  // Check if user needs to complete identification (OAuth flow)
  const needsIdentification = onboarding.requiredActions?.includes('complete_identification')

  const handleEmailCheckClick = () => {
    setShowEmailModal(true)
  }

  const handleCloseEmailModal = () => {
    setShowEmailModal(false)
  }

  const handleIdentificationClick = async () => {
    try {
      app.setProgress({
        message: i18n.t('banking.accounts.membership_setup.authorizing'),
      })

      const currentUrl = window.location.origin + window.location.pathname
      const authorizationUrl = await getAuthorizationUrl(employee.id, currentUrl, {
        action: 'retry_identification',
        targetId: onboarding.id,
      })

      if (authorizationUrl) {
        // Redirect to Swan for identification
        window.location.href = authorizationUrl
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('banking.accounts.membership_setup.error'),
        })
        app.setProgress(null)
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('banking.accounts.membership_setup.error'),
      })
      app.setProgress(null)
    }
  }

  // Determine handler based on required actions
  const handleClick = needsEmailCheck
    ? handleEmailCheckClick
    : needsIdentification
      ? handleIdentificationClick
      : undefined

  return (
    <>
      <AccountsPageCard
        logo={bankLogo(onboarding, getDefaultAlias(onboarding.creationSource))}
        statusType={mapStatusToClass(status)}
        status={mapStatusToText(status)}
        title={getDefaultAlias(onboarding.creationSource)}
        description={legalEntityName}
        to={needsEmailCheck || needsIdentification ? undefined : url}
        linkText={linkText}
        onClick={handleClick}
      />

      {showEmailModal && (
        <Modal
          illustration="alert"
          title={i18n.t('banking.onboarding.check_email_modal.title')}
          description={i18n.t('banking.onboarding.check_email_modal.description')}
          onClose={handleCloseEmailModal}
          footer={
            <Button onClick={handleCloseEmailModal} type="primary">
              {i18n.t('banking.onboarding.check_email_modal.button')}
            </Button>
          }
        />
      )}
    </>
  )
}
